import React, { useState } from "react";
import {
  Modal,
  Form,
  notification,
  Upload,
  Row,
  Col,
  Input,
  Button,
} from "antd";
import Joi from "joi";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import config from "../../../../config/config";
import helper from "../../../../helpers/helper";

const Create = ({
  isVisible,
  handleClose,
  moduleName,
  base_url,
  existingCount,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState({});
  const [uploadedCount, setUploadedCount] = useState(existingCount);

  const sizeLimit = config.sizeLimit;
  const allowedExtensions = config.allowed_extensions;
  const joiOptions = config.joiOptions;

  // ---------- VALIDATION ----------
  const validate = (value) => {
    let schemaObj = {
      webImg: Joi.required().label("Web Image"),
      mobileImg: Joi.required().label("Mobile Image"),
      iconImg: Joi.required().label("Icon Image"),
      title: Joi.string().required().label("Title"),
    };

    const schema = Joi.object(schemaObj).options({ allowUnknown: true });
    const { error } = schema.validate(value, joiOptions);

    const errors = {};
    if (error) {
      error.details.forEach((field) => {
        errors[field.path[0]] = field.message;
      });
    }

    helper.validateSize("webImg", value.webImg, sizeLimit, errors);
    helper.validateExt("webImg", value.webImg, allowedExtensions, errors);

    helper.validateSize("mobileImg", value.mobileImg, sizeLimit, errors);
    helper.validateExt("mobileImg", value.mobileImg, allowedExtensions, errors);

    helper.validateSize("iconImg", value.iconImg, sizeLimit, errors);
    helper.validateExt("iconImg", value.iconImg, allowedExtensions, errors);

    return errors;
  };

  // ---------- SUBMIT ----------
  const handleSubmit = async (value) => {
    const errors = validate(value);
    setFormError(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    const formData = new FormData();

    // Backend expects:
    // sectionTitle
    // benefits: [{ title, webImg, mobileImg, iconImg }]
    const benefitData = [
      {
        title: value.title,
        webImg: "temp",
        mobileImg: "temp",
        iconImg: "temp",
      },
    ];

    formData.append("sectionTitle", "OUR BENEFITS");
    formData.append("benefits", JSON.stringify(benefitData));

    formData.append("webImg_0", value.webImg.file);
    formData.append("mobileImg_0", value.mobileImg.file);
    formData.append("iconImg_0", value.iconImg.file);

    try {
      const response = await axios.post(`${base_url}addBenefits`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response?.data?.message) {
        notification.success({
          message: `${moduleName} Added Successfully`,
          placement: "topRight",
        });

        form.resetFields();
        handleClose(true);
      } else {
        notification.error({
          message: response?.data?.message || "Failed to add record",
          placement: "topRight",
        });
      }
    } catch (error) {
      notification.error({
        message: error?.response?.data?.message || "Something went wrong",
        placement: "topRight",
      });
    }

    setLoading(false);
  };

  const uploadProps = {
    beforeUpload: () => false,
    maxCount: 1,
    listType: "picture-card",
    showUploadList: { showPreviewIcon: false, showRemoveIcon: true },
  };

  const recommendedSizes = [
    "HEALTH COVERAGE - </br> Desktop - 778 × 440",
    "RETIREMENT BENEFITS -</br>  Desktop - 400 × 440",
    "PAID TIME OFF (PTO) -</br>  Desktop - 400 × 440",
    "FLEX WORK SCHEDULE - Desktop - 1020 × 440",
    "CAREER DEVELOPMENT OPPORTUNITIES - Desktop - </br> 585 × 440",
  ];

  return (
    <Modal
      title={`Add ${moduleName}`}
      open={isVisible}
      onCancel={() => handleClose(false)}
      okText="Create"
      cancelText="Cancel"
      confirmLoading={loading}
      width={700}
      onOk={() => {
        form
          .validateFields()
          .then((values) => handleSubmit(values))
          .catch(() => {});
      }}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              required
              label="Desktop Image"
              name="webImg"
              // extra={`${recommendedSizes[existingCount] || "N/A"}`}
              extra={
                <span
                  dangerouslySetInnerHTML={{
                    __html: recommendedSizes[existingCount] || "N/A",
                  }}
                />
              }
              {...(formError.webImg && {
                help: formError.webImg,
                validateStatus: "error",
              })}
            >
              <Upload {...uploadProps}>
                <PlusOutlined />
              </Upload>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              required
              label="Mobile Image"
              name="mobileImg"
              extra="Mobile - 345 × 225"
              {...(formError.mobileImg && {
                help: formError.mobileImg,
                validateStatus: "error",
              })}
            >
              <Upload {...uploadProps}>
                <PlusOutlined />
              </Upload>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              required
              label="Icon"
              name="iconImg"
              extra="Logo size - 68 × 68"
              {...(formError.iconImg && {
                help: formError.iconImg,
                validateStatus: "error",
              })}
            >
              <Upload {...uploadProps}>
                <PlusOutlined />
              </Upload>
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              required
              label="Title"
              name="title"
              {...(formError.title && {
                help: formError.title,
                validateStatus: "error",
              })}
            >
              <Input placeholder="Enter Title" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default Create;
